import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Sparkles, Check, Rocket } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const WhatsNewModal = () => {
  const { isAuthenticated, token } = useAuth();
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    if (isAuthenticated && token) {
      // Don't show "What's New" modal on pricing page (e.g. right after registration)
      if (window.location.pathname === '/pricing') return;
      checkForUpdates();
    }
  }, [isAuthenticated, token]);

  const checkForUpdates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/release-notes/unseen`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.length > 0) {
          setNotes(data);
          setOpen(true);
        }
      }
    } catch (err) {
      console.error('Failed to check release notes:', err);
    }
  };

  const handleClose = async () => {
    setOpen(false);
    try {
      await fetch(`${API_URL}/api/release-notes/mark-seen`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
    } catch (err) {
      console.error('Failed to mark notes seen:', err);
    }
  };

  if (notes.length === 0) return null;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent 
        className="max-w-lg max-h-[85vh] overflow-y-auto" 
        data-testid="whats-new-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        hideClose
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Sparkles className="w-6 h-6 text-yellow-500" />
            What's New in PropertyARV
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {notes.map((note) => (
            <div key={note.version} className="space-y-3">
              <div className="flex items-center gap-3">
                <Badge className="bg-gradient-to-r from-purple-600 to-pink-500 text-white border-0 text-sm px-3 py-1">
                  v{note.version}
                </Badge>
                <h3 className="font-semibold text-gray-900 dark:text-white">{note.title}</h3>
              </div>
              <ul className="space-y-2 ml-1">
                {note.items.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2.5 text-sm text-gray-700 dark:text-gray-300">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-gray-400 dark:text-gray-500">{note.date}</p>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button onClick={handleClose} className="w-full bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white" data-testid="whats-new-close-btn">
            <Rocket className="w-4 h-4 mr-2" />
            Got it, let's go!
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WhatsNewModal;
